import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import { cls } from "app/utils";
import arrow from "app/images/calendar-settings-arrow.svg";
import GearSpotlight from "./GearSpotlight";
import styles from "./styles.module.scss";

interface CalendarSettingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  /** The toolbar's gear button, spotlighted above the overlay while open. */
  settingsButtonRef: React.RefObject<HTMLElement>;
}

/**
 * First-visit popup pointing an Academy coach at the calendar's gear button
 * (Figma 410:21980). The illustration is a greyed copy of the toolbar built
 * from the same icon font as the real one, so it stays translatable; only the
 * curved arrow is an exported asset.
 */
const CalendarSettingsPopup = ({
  isOpen,
  onClose,
  settingsButtonRef,
}: CalendarSettingsPopupProps) => {
  const { t } = useTranslation();
  const ctaRef = useRef<HTMLButtonElement>(null);

  // Focus moves into the dialog on open; the toolbar beneath stays covered.
  useEffect(() => {
    if (isOpen && ctaRef.current) {
      ctaRef.current.focus();
    }
  }, [isOpen]);

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      classNames={styles.modal_container}
      data-testid="calendar-settings-popup"
      dialogLabel={t("Calendar Settings")}
    >
      <GearSpotlight anchorRef={settingsButtonRef} />
      <div className={styles.illustration} aria-hidden="true">
        <div className={styles.toolbar}>
          <div className={styles.toolbar_group}>
            <span className={cls(styles.icon_button, styles.rotate_prev)}>
              <i className="ico ico-chevron" />
            </span>
            <span className={styles.pill_button}>{t("Today")}</span>
            <span className={cls(styles.icon_button, styles.rotate_next)}>
              <i className="ico ico-chevron" />
            </span>
          </div>
          <span className={styles.view_chip}>
            <i className={cls("ico ico-cal-week", styles.view_icon)} />
            <span className={styles.view_label}>{t("Week")}</span>
            <i className={cls("ico ico-chevron", styles.view_chevron)} />
          </span>
          <span className={styles.divider} />
          <div className={styles.toolbar_group}>
            <span className={styles.pill_button}>+ {t("Add")}</span>
            <span className={cls(styles.icon_button, styles.gear_button)}>
              <i className="ico ico-cog" />
            </span>
          </div>
        </div>
        <img className={styles.arrow} src={arrow} alt="" />
        <span className={styles.tooltip}>
          {t("All your calendar settings are here")}
        </span>
        <div className={styles.setup_row}>
          <span className={styles.setup_label}>{t("Setup your Calendar")}</span>
          <span className={styles.setup_pill}>{t("Booking Options")}</span>
          <span className={styles.setup_pill}>{t("Availability")}</span>
        </div>
      </div>
      <div className={styles.text_content}>
        <h2 className={styles.title}>{t("Calendar Settings")}</h2>
        <p className={styles.description}>
          {t("Configure your calendar settings by clicking the gear icon.")}
        </p>
      </div>
      <button
        ref={ctaRef}
        type="button"
        className={styles.cta_button}
        onClick={onClose}
      >
        {t("Got it!")}
      </button>
    </ModalLayout>
  );
};

export default CalendarSettingsPopup;
